<template>
  <div class="steps-wrapper">
    <h3 class="step-title">{{steps[currentStep]['pageTitle']}}</h3>

    <step-one
        v-if="currentStep === 1"
        :currentAdvertiserId="currentAdvertiserId"
        ref="stepOne"
        :selectedRetailerIds="selectedRetailerIds"
        :merchantSponsorRequestId="merchantSponsorRequestId"
        :disableLists="disableLists"
        @setPageErrors="setErrors"
        @updateAd="updateAd">
    </step-one>
    <step-two
        v-else-if="currentStep === 2"
        :ad="ad"
        :isEdit="isEdit"
        ref="stepTwo"
        :selectedRetailerIds="selectedRetailerIds"
        :currentAdvertiserId="currentAdvertiserId"
        :selectedRetailers.sync="selectedRetailers"
        :smsRate="smsRate"
        @setData="setData"
        @setPageErrors="setErrors"
        @updateAd="updateAd">
    </step-two>
    <step-three
        v-else-if="currentStep === 3"
        :ad="ad"
        :imageLoading.sync="imageLoading"
        :currentAdvertiserId="currentAdvertiserId"
        :isEdit="isEdit"
        ref="stepThree"
        :selectedRetailers="selectedRetailers"
        @loadAd="$emit('loadAd')"
        @setPageErrors="setErrors"
        @updateAd="updateAd">
    </step-three>
    <step-four
        v-else-if="currentStep === 4"
        :ad="ad"
        :adSent="adSent"
        :isEdit="isEdit"
        ref="stepFour"
        @setPageErrors="setErrors"
        @updateAd="updateAd">
    </step-four>
    <modal
        v-if="showModal"
        buttonText="confirm"
        showClose
        :wrapperClasses="modalConfig.wrapperClasses"
        @close="showModal = false"
        @proceed="processModalAction(modalConfig.action)">
      <div v-if="modalConfig.isCostModal" slot="body" class="cost-preview-wrapper">
        <h3 class="title">review potential pricing</h3>
        <h3 class="sub-title">there's no way to know which retailers will choose to run your ad, this is a breakdown of how much our ad would cost by invited retailer.</h3>
        <p class="max-cost">max potential cost on {{ abbrNum(recipientMax, 1) }} sms = <span>${{abbrNum(parseFloat(maxCost), 1) }} </span></p>
        <table>
          <thead>
            <th>retailer</th>
            <th>cost of sms credits</th>
          </thead>
          <tbody>
            <tr v-for="(retailer, index) in selectedRetailers" :key="index">
              <td>{{retailer.name}}</td>
              <td>{{abbrNum(retailer.recipient_sms_size, 1)}} customers x ${{smsRate}} per message = <span> ${{ abbrNum((retailer.recipient_sms_size * smsRate), 1)}} </span>  </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else slot="header" class="header">{{modalConfig.header}}</div>
    </modal>
    <div class="action-buttons">
      <div class="buttons-wrapper">
        <div @click="checkForErrors" class="button">{{steps[currentStep]['action']}}</div>
      </div>
      <div v-if="isEdit" class="edit-ad-controls">
        <div class="delete-link" @click="setConfirmModal">end ad</div>
        <div v-if="currentStep !== 4" class="done-link" @click="doneEditingAd">done</div>
      </div>
      <div v-else class="delete-link" @click="cancelAd">cancel</div>
    </div>
  </div>
</template>

<script>
  import LoadRetailers from '../mixins/LoadRetailers.js'
  import Modal from '../Shared/Modal'
  import moment from 'moment'
  import StepFour from './StepFour'
  import StepOne from './StepOne'
  import StepThree from './StepThree'
  import StepTwo from './StepTwo'
  import { NumberFormatters } from '../mixins/NumberFormatters.js'

  export default {
    props: {
      ad: Object,
      currentAdvertiserId: [Number, String],
      currentStep: Number,
      isEdit: Boolean,
      adSent: Boolean,
      steps: Object,
      imageLoading: Boolean
    },

    name: 'Steps',

    mixins: [LoadRetailers, NumberFormatters],

    components: {
      Modal,
      StepOne,
      StepTwo,
      StepThree,
      StepFour
    },

    data() {
      return {
        availableRetailers: [],
        maxCost: null,
        modalConfig: {},
        recipientMax: null,
        selectedRetailerIds: [],
        showModal: false,
        smsRate: null,
        selectedRetailers: [],
        textToUpdate: null,
        merchantSponsorRequestId: []
      }
    },

    computed: {
      disableLists() {
        return this.isEdit && !!this.merchantSponsorRequestId?.length
      }
    },

    methods: {
      cancelAd() {
        this.$router.push({path:'/'})
      },

      checkForErrors() {
        switch (this.currentStep) {
          case 1:
            if (!this.selectedRetailerIds?.length && !this.merchantSponsorRequestId?.length) {
              this.$emit('setPageErrors', 'adApi', 'must select at least one retailer or sponsor request')
            }
            break
          case 2:
            this.$refs?.stepTwo?.validateRecipientMax()
            break
          case 3:
            this.$refs?.stepThree?.validateName()
            this.$refs?.stepThree?.validateImage()
            break
          case 4:
            this.$refs?.stepFour?.validateTimeframe()
            this.$refs?.stepFour?.validateTrackingHours()
            break
        }

        this.$emit('validateStep', this.steps[this.currentStep]['action'])
      },

      doneEditingAd() {
        switch(this.currentStep) {
          case 1:
            if (!this.selectedRetailerIds?.length && !this.merchantSponsorRequestId?.length) {
              this.$emit('setPageErrors', 'adApi', 'must select at least one retailer or sponsor request')
              return
            }
            break
          case 3:
            const hasValidName = !!this.ad.name
            if (!hasValidName) {
              this.setErrors('adName', 'must enter a name')
              return
            }
            break
        }

        this.$router.push({path: `/performance/${this.ad.id}`})
      },

      imageLoadingErrorHandler() {
        this.$refs.stepThree?.imageLoadingErrorHandler()
      },

      processModalAction(action) {
        let dataToUpdate
        if (action === 'endAd') {
          dataToUpdate = { send_at_end: moment().format('YYYY/MM/DD HH:mm:00') }
        } else if (action === 'completeAd') {
          dataToUpdate = { status: 1 }
          // Add this param to send email to notify that sponsor request is fulfilled
          if (!this.isEdit && !!this.merchantSponsorRequestId?.length) {
            dataToUpdate.send_request_fulfilled_email = true
          }
        }
        if (dataToUpdate) { this.updateAd(dataToUpdate, null, true) }
        this.doneEditingAd();
      },

      setCostModal() {
        this.modalConfig = {
          action: 'completeAd',
          isCostModal: true
        }
        this.showModal = true
      },

      setConfirmModal() {
        this.modalConfig = {
          action: 'endAd',
          header: 'are you sure you want to end this ad?',
          wrapperClasses: 'warning-modal',
        }
        this.showModal = true
      },

      setData(field, value) {
        this[field] = value
      },

      setErrors(errorKey, message) {
        this.$emit('setPageErrors', errorKey, message)
      },

      setValues() {
        const hasSponsorRequest = this.ad.marketplace_sponsor_request_id != null
        this.selectedRetailerIds = hasSponsorRequest ? [] : this.ad.retailers
        this.merchantSponsorRequestId = hasSponsorRequest ? [this.ad.marketplace_sponsor_request_id] : []
        this.smsRate = this.ad.cost_per_message
      },

      updateAd(dataToUpdate, additionalParams, isForCompletion) {
        this.$emit('updateAd', dataToUpdate, additionalParams, isForCompletion)
      },
    },

    watch: {
      ad: function () {
        this.setValues();
      },
    },
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.steps-wrapper
  height: 100%
  margin: 1rem
  position: relative
  text-align: center

  .step-title
    margin: 0 auto 2rem auto

  .subtitle
    color: $title-color
    font-size: 1.25rem
    margin: -1rem auto 0 auto
    max-width: 500px

    &.small
      color: $title-color
      font-size: .9rem
      margin: -1.5rem auto 2rem auto

  .action-buttons
    align-items: flex-end
    display: flex
    flex-direction: column
    justify-content: flex-end
    margin: 2rem
    position: relative
    bottom: 2rem
    pointer-events: none


    .buttons-wrapper
      pointer-events: auto

    @media only screen and (max-width: 40em)
      margin-right: 0

    .edit-ad-controls
      display: flex
      justify-content: space-between
      width: 100%

      .done-link
        cursor: pointer
        pointer-events: auto
        margin-top: 2rem
        font-size: .9rem
        color: $primary-color
        &:hover
          color: $tertiary-color

    .delete-link
      color: $negative-color
      cursor: pointer
      pointer-events: auto
      margin-top: 2rem
      &:hover
        color: $negative-hover

    .button
      font-size: 1.5rem
      padding: 1.5rem 4rem
      @media only screen and (max-width: 40em)
        font-size: 1.25rem
        padding: 1rem 3rem

      &:after
        border-bottom: 1px solid #fff
        border-right: 1px solid #fff
        content: ' '
        display: inline-block
        height: 15px
        margin-left: 1rem
        transform: rotate(-45deg)
        width: 15px
        @media only screen and (max-width: 40em)
          height: 12px
          margin-left: .5rem
          width: 12px
</style>
